/**
 * D-EZ-107 — WHO THE DRIVERS ARE.
 *
 * Every driver gets a temperament — temper (ice-cold 1 ↔ hothead 5), ambition,
 * loyalty, confidence — a way with the press, a voice his quotes are written
 * in, and, if he is one of the league's own (fictional) drivers, a one-line
 * story of where he came up.
 *
 * ⚠ NARRATIVE ONLY (THE GOVERNING RULE, D-EZ-104…111). A personality is derived
 * from a hash of the league and the driver id — never a die from the engine —
 * and nothing in the engine reads it. A hothead does not wreck more often; he
 * just says more about it when the dice wreck him.
 *
 * ⚠ IT GROWS WITH HIS CAREER, AND IT IS NOT STORED. A first title makes him
 * surer of himself; a long drought frays his temper; years with one shop make
 * him loyal to it. Each shift is read from the league's history as of the
 * moment asked (`shiftsFor`), so a personality is always a function of
 * (league, driver, history so far) and needs no save field.
 *
 * ⚠ REAL DRIVERS (the 2025 roster, `rw25-…`). They get temperament and manner
 * only — how this league's dice have made them look — and NO backstory: the
 * game does not invent biography for real people. Their quotes are not written
 * either (`quotes.ts`): no words are put in a real man's mouth.
 */

import { hometownFor, isRealDriver, stableHash } from '../story/hometown.ts';
import type { RegistryEntry } from '../history/registry.ts';
import type { DriverStreaks } from '../history/milestones.ts';
import type { RivalryBook } from './rivalry.ts';

export type Manner = 'quotable' | 'plainspoken' | 'surly' | 'corporate';
export type Voice = 'hothead' | 'ice' | 'corporate' | 'surly' | 'quotable' | 'plain';
export type TraitName = 'temper' | 'ambition' | 'loyalty' | 'confidence';

export interface PersonalityShift {
  trait: TraitName;
  by: number;
  /** Why, in plain words, from the record. */
  why: string;
}

export interface Personality {
  driverId: string;
  real: boolean;
  /** 1 = ice in his veins … 5 = short fuse. After the shifts. */
  temper: number;
  ambition: number;
  loyalty: number;
  confidence: number;
  manner: Manner;
  voice: Voice;
  /** What he was when he arrived, before his career moved him. */
  born: Record<TraitName, number>;
  shifts: PersonalityShift[];
  /** Where he came up — a key into ORIGINS; null for a real driver. */
  cameUp: string | null;
  /** One line; null for a real driver. */
  backstory: string | null;
  /** The shop he is loyal to, when he is (loyalty 5 with a shop). */
  loyalTo: string | null;
  /** Plain words a card prints: "Short fuse", "Ice in his veins", … */
  traits: string[];
}

/** What a personality can grow from. All optional — no history, no shifts. */
export interface PersonalityHistory {
  entry?: RegistryEntry | null;
  streak?: DriverStreaks | null;
  book?: RivalryBook | null;
  /** His age now, when known. */
  age?: number | null;
}

// ---------------------------------------------------------------------------
// The hash
// ---------------------------------------------------------------------------

function pick<T>(seed: string, options: readonly (readonly [T, number])[]): T {
  const total = options.reduce((t, [, w]) => t + w, 0);
  let r = stableHash(seed) % total;
  for (const [v, w] of options) {
    if (r < w) return v;
    r -= w;
  }
  return options[options.length - 1][0];
}

const scale = (weights: readonly number[]): (readonly [number, number])[] => weights.map((w, i) => [i + 1, w] as const);

const TEMPER = scale([12, 24, 30, 22, 12]);
const AMBITION = scale([8, 20, 34, 24, 14]);
const LOYALTY = scale([10, 20, 36, 22, 12]);
const CONFIDENCE = scale([8, 20, 36, 24, 12]);

function mannerFor(seed: string, temper: number): Manner {
  if (temper >= 4) return pick<Manner>(`${seed}|manner`, [['quotable', 40], ['surly', 35], ['plainspoken', 15], ['corporate', 10]]);
  if (temper === 3) return pick<Manner>(`${seed}|manner`, [['quotable', 25], ['plainspoken', 35], ['corporate', 25], ['surly', 15]]);
  return pick<Manner>(`${seed}|manner`, [['corporate', 40], ['plainspoken', 40], ['quotable', 15], ['surly', 5]]);
}

export function voiceFor(temper: number, manner: Manner): Voice {
  if (manner === 'corporate') return 'corporate';
  if (temper >= 4 && (manner === 'quotable' || manner === 'plainspoken')) return 'hothead';
  if (temper <= 2 && (manner === 'plainspoken' || manner === 'surly')) return 'ice';
  if (manner === 'quotable') return 'quotable';
  if (manner === 'surly') return 'surly';
  return 'plain';
}

// ---------------------------------------------------------------------------
// Where he came up — fiction, for fictional drivers only.
// ---------------------------------------------------------------------------

/**
 * `{home}` is his hometown (`hometown.ts`), the same town every screen prints.
 * `minAge` keeps a line from contradicting his card (no "late bloomer" at 23).
 */
export const ORIGINS: readonly { key: string; line: string; minAge?: number }[] = [
  { key: 'bullrings', line: 'Came up on the quarter-mile bullrings around {home}.' },
  { key: 'dirt', line: 'Learned his trade on the red-clay dirt tracks around {home}.' },
  { key: 'family-team', line: 'Grew up in a family team out of {home} — his daddy still builds the engines.' },
  { key: 'crew-chiefs-son', line: "An old crew chief's son who grew up sweeping the shop floor in {home}." },
  { key: 'late-bloomer', line: 'Turned wrenches in a {home} garage until he was nearly thirty — a late bloomer.', minAge: 30 },
  { key: 'modifieds', line: 'Ran modifieds on Saturday nights out of {home} before anybody here knew his name.' },
  { key: 'motorcycles', line: 'Raced motorcycles in the hills around {home} before he ever sat in a stock car.' },
  { key: 'hobby-stock', line: 'Won a hobby-stock title at his home track in {home} and never looked back.' },
  { key: 'army', line: 'Came home to {home} from the Army and went straight to the race track.' },
  { key: 'go-karts', line: 'Was winning go-kart races around {home} before he was old enough to drive on the highway.' },
  { key: 'tobacco-farm', line: 'Grew up on a tobacco farm outside {home} and raced anything that had wheels.' },
  { key: 'wrecker-driver', line: 'Drove a wrecker in {home}, pulling other men out of ditches, until a car owner gave him a chance.', minAge: 27 },
  { key: 'quarterback', line: 'A high-school quarterback in {home} who traded the football for a steering wheel.' },
  { key: 'late-models', line: 'Cut his teeth in late models across the Carolinas, sleeping in the hauler between races.' },
  { key: 'speedway-gate', line: 'Sold programs at the gate of his home speedway near {home} and swore he would race there one day.' },
  { key: 'bootstrap', line: 'Built his first race car out of a junkyard in {home} with money from mowing lawns.' },
];

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

const TEMPER_WORDS: Record<number, string | null> = { 5: 'Short fuse', 4: 'Runs hot', 3: null, 2: 'Cool head', 1: 'Ice in his veins' };
const AMBITION_WORDS: Record<number, string | null> = { 5: 'Wants it all', 4: 'Hungry', 3: null, 2: 'Happy where he is', 1: 'Races for the love of it' };
const LOYALTY_WORDS: Record<number, string | null> = { 5: 'Fiercely loyal', 4: 'A company man', 3: null, 2: 'Always looking at the next ride', 1: 'A hired gun' };
const CONFIDENCE_WORDS: Record<number, string | null> = {
  5: "Thinks he's the best out there",
  4: 'Sure of himself',
  3: null,
  2: 'Second-guesses himself',
  1: 'Doubts himself',
};
const MANNER_WORDS: Record<Manner, string> = {
  quotable: 'Good for a quote',
  surly: 'Short with the press',
  corporate: 'Thanks the sponsors first',
  plainspoken: 'Man of few words',
};

export const TRAIT_SCALE_WORDS = { temper: TEMPER_WORDS, ambition: AMBITION_WORDS, loyalty: LOYALTY_WORDS, confidence: CONFIDENCE_WORDS };

const clamp = (n: number) => Math.max(1, Math.min(5, n));

// ---------------------------------------------------------------------------
// How a career moves him
// ---------------------------------------------------------------------------

export function shiftsFor(driverId: string, h: PersonalityHistory): PersonalityShift[] {
  const out: PersonalityShift[] = [];
  const e = h.entry ?? null;
  const st = h.streak ?? null;
  if (e) {
    const titles = e.bySeason.filter((l) => l.champion).map((l) => l.seasonNumber);
    if (titles.length >= 1) out.push({ trait: 'confidence', by: 1, why: `Champion in season ${titles[0]}.` });
    if (titles.length >= 3) out.push({ trait: 'confidence', by: 1, why: `A ${titles.length}-time champion.` });
    else if (e.league.wins >= 10) out.push({ trait: 'confidence', by: 1, why: `${e.league.wins} wins in this league.` });
    if (e.league.wins === 0 && e.league.starts >= 72) {
      out.push({ trait: 'confidence', by: -1, why: `${e.league.starts} starts and still looking for the first win.` });
    }
    // Loyalty: the same shop, season after season — or a new one every winter.
    const shops = e.bySeason.map((l) => l.teamId).filter((t): t is string => t !== null).map((t) => t.replace(/-s\d+$/, ''));
    const last = shops.slice(-4);
    if (last.length >= 4 && last.every((t) => t === last[0])) out.push({ trait: 'loyalty', by: 1, why: `${last.length} seasons with ${e.teamName ?? 'one shop'}.` });
    let moves = 0;
    for (let i = 1; i < shops.length; i++) if (shops[i] !== shops[i - 1]) moves += 1;
    if (moves >= 2) out.push({ trait: 'loyalty', by: -1, why: `${moves + 1} different shops.` });
  }
  if (st?.inDrought) {
    out.push({ trait: 'temper', by: 1, why: `${st.startsSinceWin} starts since his last win.` });
    out.push({ trait: 'confidence', by: -1, why: 'The drought is getting to him.' });
  }
  if (h.age !== null && h.age !== undefined && h.age >= 40) out.push({ trait: 'temper', by: -1, why: `The years have cooled him — he is ${h.age}.` });
  if (h.book) {
    const boiling = h.book.rivalries.find((r) => r.state === 'boiling' && (r.a === driverId || r.b === driverId));
    if (boiling) out.push({ trait: 'temper', by: 1, why: `His rivalry with ${boiling.a === driverId ? boiling.bName : boiling.aName} is boiling.` });
  }
  return out;
}

// ---------------------------------------------------------------------------
// The personality
// ---------------------------------------------------------------------------

/**
 * The man, derived. `leagueKey` keeps two leagues' "drv-012" from being the same
 * person (ids repeat across leagues; use `leagueKeyOf` from `context.ts`).
 */
export function derivePersonality(
  driverId: string,
  leagueKey: string,
  history: PersonalityHistory = {},
  teamName: string | null = null,
): Personality {
  const real = isRealDriver(driverId);
  const seed = `persona|${leagueKey}|${driverId}`;
  const born: Record<TraitName, number> = {
    temper: pick(`${seed}|temper`, TEMPER),
    ambition: pick(`${seed}|ambition`, AMBITION),
    loyalty: pick(`${seed}|loyalty`, LOYALTY),
    confidence: pick(`${seed}|confidence`, CONFIDENCE),
  };
  const manner = mannerFor(seed, born.temper);
  const shifts = shiftsFor(driverId, history);
  const now = { ...born };
  for (const s of shifts) now[s.trait] = clamp(now[s.trait] + s.by);

  let cameUp: string | null = null;
  let backstory: string | null = null;
  if (!real) {
    const age = history.age ?? null;
    const startAge = age !== null && history.entry ? age - (history.entry.lastSeason - history.entry.firstSeason) : age;
    const fits = ORIGINS.filter((o) => o.minAge === undefined || (startAge !== null && startAge >= o.minAge));
    const origin = fits[stableHash(`${seed}|origin`) % fits.length];
    const home = hometownFor(driverId);
    cameUp = origin.key;
    backstory = origin.line.replace(/\{home\}/g, home ? home.replace(/,.*$/, '') : 'home');
  }

  const shop = teamName ?? history.entry?.teamName ?? null;
  const loyalTo = now.loyalty === 5 && shop ? shop : null;
  const traits: string[] = [];
  const t = TEMPER_WORDS[now.temper];
  if (t) traits.push(t);
  const c = CONFIDENCE_WORDS[now.confidence];
  if (c) traits.push(c);
  const a = AMBITION_WORDS[now.ambition];
  if (a) traits.push(a);
  const l = LOYALTY_WORDS[now.loyalty];
  if (l) traits.push(loyalTo ? `Fiercely loyal to ${loyalTo}` : l);
  traits.push(MANNER_WORDS[manner]);

  return {
    driverId,
    real,
    temper: now.temper,
    ambition: now.ambition,
    loyalty: now.loyalty,
    confidence: now.confidence,
    manner,
    voice: voiceFor(now.temper, manner),
    born,
    shifts,
    cameUp,
    backstory,
    loyalTo,
    traits,
  };
}
